/**
 * VerdictBlock - Bottom Line Up Front (BLUF) display
 * 
 * Shows the main verdict with supporting context:
 * - BLUF statement
 * - So-what implications
 * - Counterpoint
 * - Key assumption
 */

import React from 'react'
import { VerdictBlock as VerdictBlockType } from '../../../../lib/cockpit/schemas'

interface VerdictBlockProps {
  data: VerdictBlockType['data']
  blockId: string
  title?: string
  compact?: boolean
  showExplain?: boolean
}

const CONFIDENCE_INDICATORS = {
  very_low: { bars: 1, label: 'Very Low' },
  low: { bars: 2, label: 'Low' },
  medium: { bars: 3, label: 'Medium' },
  high: { bars: 4, label: 'High' },
  very_high: { bars: 5, label: 'Very High' },
} as const

function VerdictBlock({ data, blockId, title, compact = false, showExplain = false }: VerdictBlockProps) {
  const confidenceConfig = CONFIDENCE_INDICATORS[data.confidence as keyof typeof CONFIDENCE_INDICATORS]

  if (compact) {
    return (
      <div className="space-y-2">
        <p className="text-lg font-semibold text-gray-900 dark:text-white leading-relaxed">
          {data.bluf}
        </p>
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(bar => (
              <div
                key={bar}
                className={`w-1 h-3 rounded-sm ${
                  bar <= confidenceConfig.bars
                    ? 'bg-blue-500'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {confidenceConfig.label}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Main BLUF */}
      <div className="relative pl-4 border-l-4 border-blue-500">
        <p className="text-xl font-semibold text-gray-900 dark:text-white leading-relaxed">
          {data.bluf}
        </p>
      </div>

      {/* So What */}
      <div className="flex gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>
        <div>
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            So What
          </div>
          <p className="text-gray-700 dark:text-gray-300">
            {data.so_what}
          </p>
        </div>
      </div>

      {/* Counterpoint */}
      {data.counterpoint && (
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Counterpoint
            </div>
            <p className="text-gray-700 dark:text-gray-300 italic">
              {data.counterpoint}
            </p>
          </div>
        </div>
      )}

      {/* Key Assumption */}
      {data.key_assumption && (
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Key Assumption
            </div>
            <p className="text-gray-700 dark:text-gray-300">
              {data.key_assumption}
            </p>
          </div>
        </div>
      )}

      {/* Confidence and Evidence */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map(bar => (
                <div
                  key={bar}
                  className={`w-1.5 h-4 rounded-sm ${
                    bar <= confidenceConfig.bars
                      ? 'bg-blue-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {confidenceConfig.label} confidence
            </span>
          </div>
        </div>
        
        {data.evidence_anchors.length > 0 && (
          <button className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {data.evidence_anchors.length} source{data.evidence_anchors.length !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Explain section */}
      {showExplain && (
        <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
            <p>
              <strong className="text-gray-700 dark:text-gray-300">What it means:</strong>{' '}
              This verdict represents the primary conclusion from the analysis, synthesizing multiple lines of evidence.
            </p>
            <p>
              <strong className="text-gray-700 dark:text-gray-300">Limitations:</strong>{' '}
              This conclusion is based on available evidence and may change as new information emerges.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default VerdictBlock



